import reduceReducers from "reduce-reducers";

import {
  DEPLOYMENT_SALE,
  REMOVE_SALE_FROM_STORE,
  SALE,
  SALE_ATTRIBUTES,
  SALE_STATUS,
} from "constants/actionTypes";

import { defaultInitialState, offlineCrudReducer } from "lib/reducers";
import {
  deserializeConsignableSale,
  deserializeSale,
  deserializeDeploymentSale,
} from "lib/serializers/sale";

const initialState: SaleStateSlice = {
  ...defaultInitialState,
  consignableSales: {
    isFetching: false,
    data: {},
  },
  errorMessage: "",
};

const reducerProps = {
  deserializer: deserializeSale,
  clearOnRequest: false,
  initialState,
  deleteAfterSuccess: true,
};

const getLivestockSaleIdByDeploymentSaleId =
  (deploymentSaleId: number) => (state: SaleStateSlice) => {
    const sales = state.byId;
    const livestockSale = Object.values(sales || {}).find(sale =>
      sale.deployment_sales.find(
        deploymentSale =>
          deploymentSale.deployment_sale_id === deploymentSaleId,
      ),
    );
    return livestockSale ? livestockSale.livestocksale_id : null;
  };

const saleCrudReducer = offlineCrudReducer(SALE, reducerProps);

function fetchConsignableReducer(state, action) {
  switch (action.type) {
    case SALE.FETCH_CONSIGNABLE.REQUEST:
      return {
        ...state,
        consignableSales: {
          ...initialState.consignableSales,
          isFetching: true,
        },
      };

    case SALE.FETCH_CONSIGNABLE.SUCCESS: {
      const { payload } = action;
      const consignableSales = payload.reduce((acc, sale) => {
        acc[sale.livestocksale_id] = deserializeConsignableSale(sale);
        return acc;
      }, {});
      return {
        ...state,
        consignableSales: {
          isFetching: false,
          data: consignableSales,
        },
      };
    }

    case SALE.FETCH_CONSIGNABLE.FAILURE:
      return {
        ...state,
        consignableSales: {
          ...initialState.consignableSales,
          isFetching: false,
        },
      };

    default:
      return state;
  }
}

function deploymentSaleReducer(state: SaleStateSlice, action) {
  switch (action.type) {
    case DEPLOYMENT_SALE.UPDATE.REQUEST: {
      const { deploymentSaleId, deploymentSale, livestockSaleId } = action.meta;
      return {
        ...state,
        byId: {
          ...state.byId,
          [livestockSaleId]: {
            ...state.byId[livestockSaleId],
            deployment_sales: state.byId[livestockSaleId].deployment_sales.map(
              ds =>
                ds.deployment_sale_id === deploymentSaleId
                  ? { ...ds, ...deserializeDeploymentSale(deploymentSale) }
                  : ds,
            ),
          },
        },
      };
    }

    case DEPLOYMENT_SALE.UPDATE.SUCCESS: {
      const { meta, payload } = action;
      const { deploymentSaleId, livestockSaleId } = meta;
      return {
        ...state,
        byId: {
          ...state.byId,
          [livestockSaleId]: {
            ...state.byId[livestockSaleId],
            deployment_sales: state.byId[livestockSaleId].deployment_sales.map(
              ds =>
                ds.deployment_sale_id === deploymentSaleId
                  ? deserializeDeploymentSale(payload)
                  : ds,
            ),
          },
        },
      };
    }

    case DEPLOYMENT_SALE.COMMENT.REQUEST: {
      const { payload, id } = action;
      const livestockSaleId = getLivestockSaleIdByDeploymentSaleId(id)(state);
      const updatedLivestockSale = {
        ...state.byId[livestockSaleId],
        deployment_sales: state.byId[livestockSaleId].deployment_sales.map(
          ds =>
            ds.deployment_sale_id === id
              ? {
                  ...ds,
                  comment_ids: ds.comment_ids
                    ? [...ds.comment_ids, payload.id]
                    : [payload.id],
                }
              : ds,
        ),
      };

      return {
        ...state,
        byId: {
          ...state.byId,
          [livestockSaleId]: updatedLivestockSale,
        },
      };
    }

    case DEPLOYMENT_SALE.DELETE.REQUEST: {
      const { deploymentSaleId, livestockSaleId } = action.meta;
      return {
        ...state,
        byId: {
          ...state.byId,
          [livestockSaleId]: {
            ...state.byId[livestockSaleId],
            deployment_sales: state.byId[
              livestockSaleId
            ].deployment_sales.filter(
              ds => ds.deployment_sale_id !== deploymentSaleId,
            ),
          },
        },
      };
    }

    case SALE_STATUS.DELETE.SUCCESS: {
      // Mirror the backend SET_NULL: keep the row, only clear its status.
      const updatedSales = Object.fromEntries(
        (Object.values(state.byId || {}) as Sale[])
          .filter(sale => sale.sale_attributes?.saleStatusId === action.id)
          .map(sale => [
            sale.livestocksale_id,
            {
              ...sale,
              sale_attributes: { ...sale.sale_attributes, saleStatusId: null },
            },
          ]),
      );
      const byId = { ...state.byId, ...updatedSales };

      return { ...state, byId };
    }

    case SALE_ATTRIBUTES.CREATE.REQUEST:
    case SALE_ATTRIBUTES.UPDATE.REQUEST: {
      // Optimistic; reconciled by the next sale push (changesSince embeds
      // sale_attributes), which also corrects a failed save.
      const { payload } = action;
      const livestockSaleId = payload?.livestock_sale_id;
      if (!livestockSaleId || !state.byId?.[livestockSaleId]) {
        return state;
      }
      const sale = state.byId[livestockSaleId];
      const saleAttributes = {
        ...sale.sale_attributes,
        id: payload.id,
        livestockSaleId,
        saleStatusId: payload.sale_status_id ?? null,
        // Role edits carry the user-id lists so the chips render optimistically
        // (names resolve from the directory); status-only edits omit them and
        // keep the previous set.
        ...(payload.manager_ids !== undefined
          ? { managerIds: payload.manager_ids }
          : {}),
        ...(payload.assignee_ids !== undefined
          ? { assigneeIds: payload.assignee_ids }
          : {}),
      };
      return {
        ...state,
        byId: {
          ...state.byId,
          [livestockSaleId]: {
            ...sale,
            sale_attributes: saleAttributes,
          },
        },
      };
    }

    case REMOVE_SALE_FROM_STORE: {
      const { livestockSaleId } = action;
      if (!livestockSaleId || !state.byId?.[livestockSaleId]) {
        return state;
      }
      const byId = { ...state.byId };
      delete byId[livestockSaleId];
      return { ...state, byId };
    }

    default:
      return state;
  }
}

function copySaleReducer(state, action) {
  switch (action.type) {
    case SALE.CREATE_SELL_VIA_EXTERNAL_AGENT_SALE_FROM_SALE.SUCCESS: {
      const sale = action.payload;
      return {
        ...state,
        byId: {
          ...state.byId,
          [sale.livestocksale_id]: sale,
        },
      };
    }

    default:
      return state;
  }
}

const salesReducer = reduceReducers(
  saleCrudReducer,
  fetchConsignableReducer,
  deploymentSaleReducer,
  copySaleReducer,
);

export default salesReducer;
